import {API} from '../constants';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import {PbDispatch} from './index';
import {PbRequest} from './pbRequest';
import {pbRequestReset} from './pbRequestSlice';

export const OperationalContactSalutation = [
    'MALE',
    'FEMALE',
    'UNSPECIFIED',
] as const;

export type OperationalContactSalutationType =
    (typeof OperationalContactSalutation)[number];

export const OperationalContactRoles = [
    'OPERATIONAL_CONTACT',
    'ADMINISTRATIVE_CONTACT',
    'JANITOR',
    'RECEPTION',
    'SERVICE_PROVIDER',
    'PROPERTY_MANAGEMENT',
    'OTHER',
] as const;

export type OperationalContactRoleType =
    (typeof OperationalContactRoles)[number];

export interface OperationalContactWriteDto {
    salutation?: OperationalContactSalutationType;
    first_name?: string;
    name?: string;
    email?: string;
    phone1?: string;
    phone2?: string;
    role?: OperationalContactRoleType;
    notify_about_bsh_technician?: boolean;
}

export interface OperationalContactDto extends OperationalContactWriteDto {
    operational_contact_id: number;
    accounting_area_id: string;
    mansions: { mansion_id: number }[];
    mtime: number;
}

export interface OperationalContactCreateDto {
    operational_contact: OperationalContactWriteDto;
    mansion_ids?: number[];
}

export interface OperationalContactsListDto {
    list: OperationalContactDto[];
    mansions_with_missing_contact: number[];
}

interface OperationalContactsStore {
    operationalContacts: OperationalContactsListDto | undefined;
    writtenOperationalContact: OperationalContactDto | undefined;
}

const initialState: OperationalContactsStore = {
    operationalContacts: undefined,
    writtenOperationalContact: undefined,
} as const;

const operationalContactsSlice = createSlice({
    name: 'operationalContacts',
    initialState,
    reducers: {
        setOperationalContacts(
            state: OperationalContactsStore,
            action: PayloadAction<OperationalContactsListDto>
        ) {
            state.operationalContacts = action.payload;
        },
        setWrittenOperationalContact(
            state: OperationalContactsStore,
            action: PayloadAction<OperationalContactDto>
        ) {
            state.writtenOperationalContact = action.payload;
        },
    },
});

export function GetOperationalContacts() {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'GET' };
            const result =
                await processRequestForAccountingAreaWithReplacement<OperationalContactsListDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/operational_contacts`,
                    request,
                    PbRequest.GET_OPERATIONAL_CONTACTS
                );
            dispatch(
                operationalContactsSlice.actions.setOperationalContacts(result)
            );
        } catch {}
    };
}

export function UpdateOperationalContact(
    operationalContactId: number,
    operationalContact: OperationalContactWriteDto
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'PUT',
                body: JSON.stringify(operationalContact),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/operational_contacts/${operationalContactId}`,
                request,
                PbRequest.PUT_OPERATIONAL_CONTACT
            );
        } finally {
            await GetOperationalContacts()(dispatch);
        }
    };
}

export function CreateOperationalContact(
    operationalContact: OperationalContactWriteDto,
    mansion_ids?: number[]
) {
    return async (dispatch: PbDispatch) => {
        try {
            const data: OperationalContactCreateDto = {
                operational_contact: operationalContact,
                mansion_ids,
            };
            const request = {
                method: 'POST',
                body: JSON.stringify(data),
            };
            const result =
                await processRequestForAccountingAreaWithReplacement<OperationalContactDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/operational_contacts`,
                    request,
                    PbRequest.POST_OPERATIONAL_CONTACT
                );
            dispatch(
                operationalContactsSlice.actions.setWrittenOperationalContact(
                    result
                )
            );
        } finally {
            await GetOperationalContacts()(dispatch);
        }
    };
}

export function DeleteOperationalContact(
    operationalContactId: number,
    successCallback?: () => void
) {
    return async (dispatch: PbDispatch) => {
        let success = false;
        try {
            const request = {
                method: 'DELETE',
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/operational_contacts/${operationalContactId}`,
                request,
                PbRequest.DELETE_OPERATIONAL_CONTACT
            );
            success = true;
        } catch {
        } finally {
            await GetOperationalContacts()(dispatch);
        }
        if (success) {
            successCallback?.();
        }
    };
}

export function ResetCreateOperationalContact() {
    return pbRequestReset(PbRequest.POST_OPERATIONAL_CONTACT);
}

export function ResetPutOperationalContact() {
    return pbRequestReset(PbRequest.PUT_OPERATIONAL_CONTACT);
}

export function ResetDeleteOperationalContact() {
    return pbRequestReset(PbRequest.DELETE_OPERATIONAL_CONTACT);
}

export function ResetPutAllMansionsToOperationalContact() {
    return pbRequestReset(PbRequest.PUT_ALL_MANSIONS_REQUEST);
}

export function SetAllMansionsToOperationalContact(
    operationalContactId: number,
    mansion_ids: number[]
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'PUT',
                body: JSON.stringify({ mansion_ids }),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/operational_contacts/${operationalContactId}/mansions`,
                request,
                PbRequest.PUT_ALL_MANSIONS_REQUEST
            );
            await GetOperationalContacts()(dispatch);
        } catch {}
    };
}

export function AssignOperationalContactsToMansion(
    operational_contact_ids: number[],
    mansionId: number
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'POST',
                body: JSON.stringify({ operational_contact_ids }),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/mansions/${mansionId}/operational_contacts/${ACCOUNTING_AREA_ID_MARKER}`,
                request,
                PbRequest.PUT_ASSIGN_OPERATIONAL_CONTACTS_TO_MANSION_REQUEST
            );
        } finally {
            await GetOperationalContacts()(dispatch);
        }
    };
}

export default operationalContactsSlice.reducer;
